import * as React from "react";
import { Button, theme, Tooltip } from "antd";
import {
  DeleteOutlined,
  EnterOutlined,
  SearchOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { Flex } from "src/components/Flex";
import { Input } from "src/components/Input";
import { mono } from "src/fonts";
import { SearchInputClause, UseQuestionInputClauses } from "./definitions";
import { Clause } from "./Clause";
import { useMenuEscape } from "./ClauseMenu";
import styles from "./question-input.module.css";
import { usePopupContext } from "src/hooks/usePopup";

// Common styles for consistency

// Search operators help content for tooltip
const operatorsHelp = (
  <div>
    <p>
      Quickly find what you need with keywords. Across your system Entities and
      Attributes.
    </p>
    <p>
      <strong>Search Operators:</strong>
    </p>
    <ul style={{ margin: "4px 0", paddingLeft: "16px" }}>
      <li>
        <code>AND</code>: Match all terms (default)
      </li>
      <li>
        <code>OR</code>: Match any term
      </li>
      <li>
        <code>NOT</code>: Exclude terms
      </li>
    </ul>
    <p
      style={{
        marginTop: "8px",
        fontSize: "12px",
        color: "var(--color-text-tertiary)",
      }}
    >
      Example: <code>bug NOT resolved</code>
    </p>
  </div>
);

export function SearchTermClause({
  clause,
  removeClause,
  updateClause,
}: {
  clause: SearchInputClause;
} & Pick<UseQuestionInputClauses, "removeClause" | "updateClause">) {
  return (
    <Clause
      onRemove={() => removeClause(clause)}
      onMenuClose={() => !clause.value && removeClause(clause)}
      renderMenu={() => (
        <SearchTermMenu updateClause={updateClause} clause={clause} />
      )}
      clause={clause}
      title="Search term"
      icon={<SearchOutlined size={10} style={{ opacity: 0.7 }} />}
    >
      <Flex inline start gap={4}>
        {(clause.value || "").split(" ").map((term, i) =>
          ["AND", "OR", "NOT"].includes(term) ? (
            <span key={i} className={styles.keyword}>
              {term}{" "}
            </span>
          ) : (
            <span key={i} className={styles.text}>
              {term}{" "}
            </span>
          )
        )}
      </Flex>
    </Clause>
  );
}

function SearchTermMenu({
  clause,
  updateClause,
}: {
  clause: SearchInputClause;
  updateClause: UseQuestionInputClauses["updateClause"];
}) {
  const { token } = theme.useToken();
  const popup = usePopupContext();
  useMenuEscape();

  // Example searches for different scenarios
  const examples = [
    { term: "bug fix", description: "Find items about bug fixes" },
    {
      term: "feature OR enhancement",
      description: "Find new features or enhancements",
    },
    { term: "critical NOT resolved", description: "Find open critical items" },
  ];

  const handleApply = () => {
    updateClause(clause);
    popup.close();
  };

  const handleCancel = () => {
    popup.close();
  };

  return (
    <div className={styles.clausePanel}>
      {/* Search Input Section */}
      <div style={{ width: "100%" }}>
        <div className={styles.labelWrapper}>
          Search
          <Tooltip
            title={operatorsHelp}
            placement="right"
            overlayStyle={{ maxWidth: 300 }}
          >
            <QuestionCircleOutlined className={styles.tooltipIcon} />
          </Tooltip>
        </div>

        <Input
          autoFocus
          autoSelect={Boolean(clause.value)}
          style={{ width: "100%" }}
          placeholder="Enter search terms..."
          value={clause.value || ""}
          onChange={(term) => updateClause({ ...clause, value: term })}
          allowClear
        />
      </div>

      {/* Examples Panel */}
      <div className={styles.examplesPanel}>
        <div style={{ fontWeight: 500, marginBottom: "8px" }}>
          Try these examples:
        </div>
        {examples.map((example, index) => (
          <div
            key={index}
            style={{
              padding: "8px",
              cursor: "pointer",
              borderRadius: "4px",
              marginBottom: "4px",
              backgroundColor: "transparent",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = token.colorBgTextHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
            onClick={() => updateClause({ ...clause, value: example.term })}
          >
            <code style={{ color: token.colorPrimary }}>{example.term}</code>
            <div
              style={{
                fontSize: "12px",
                color: token.colorTextDescription,
                marginTop: "2px",
              }}
            >
              {example.description}
            </div>
          </div>
        ))}
      </div>

      {/* Footer with Cancel and Apply buttons */}
      <Flex
        spaceBetween
        style={{
          marginTop: "16px",
          paddingTop: "12px",
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          justifyContent: "flex-end",
        }}
      >
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => {
              updateClause({ ...clause, value: clause.value });
              popup.close();
            }}
          >
            Apply
          </Button>
        </div>
      </Flex>
    </div>
  );
}
